import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image, Film, Music, Loader2, Check, FolderOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILE_ACCEPT = {
  image: 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml',
  video: 'video/mp4,video/quicktime,video/avi,video/webm',
  audio: 'audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/aac',
  all: 'image/*,video/*,audio/*',
};

const TYPE_ICONS = { image: Image, video: Film, audio: Music };

const MediaUploader = ({ onUpload, accept = 'all', folder = 'general', label, className = '' }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const fileRef = useRef(null);

  const acceptStr = FILE_ACCEPT[accept] || FILE_ACCEPT.all;
  const Icon = TYPE_ICONS[accept] || Upload;

  const uploadFile = useCallback(async (file) => {
    setError('');
    setUploading(true);
    setProgress(10);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));

    // Show preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }

    try {

      setProgress(30);

      const res = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      setProgress(80);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }

      const data = await res.json();
      setProgress(100);

      const fullUrl = `${API_URL}${data.url}`;
      onUpload(fullUrl, data);

      setTimeout(() => {
        setUploading(false);
        setProgress(0);
        setPreview(null);
      }, 800);
    } catch (e) {
      setError(e.message);
      setUploading(false);
      setProgress(0);
      setPreview(null);
    }
  }, [folder, onUpload]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const openLibrary = async () => {
    setShowLibrary(true);
    setLibraryLoading(true);
    try {

      const typeParam = accept !== 'all' ? `&file_type=${accept}` : '';
      const res = await fetch(`${API_URL}/api/media/?limit=24${typeParam}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setLibraryAssets(d.assets || []);
      }
    } catch (e) { console.error(e); }
    setLibraryLoading(false);
  };

  const selectFromLibrary = (asset) => {
    const fullUrl = `${API_URL}${asset.url}`;
    onUpload(fullUrl, asset);
    setShowLibrary(false);
  };

  return (
    <div className={className}>
      {/* Upload zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploading && fileRef.current?.click()}
        className={`relative cursor-pointer border-2 border-dashed rounded-lg p-4 text-center transition-all duration-200 ${
          dragOver ? 'border-gold/50 bg-gold/[0.05]' : 'border-cream/[0.08] hover:border-gold/20 hover:bg-cream/[0.02]'
        } ${uploading ? 'pointer-events-none opacity-70' : ''}`}
        data-testid="media-upload-zone"
      >
        <input ref={fileRef} type="file" accept={acceptStr} onChange={handleFileChange} className="hidden" />

        {uploading ? (
          <div className="space-y-2">
            {preview && <img src={preview} alt="preview" className="w-16 h-16 mx-auto object-cover rounded" />}
            <Loader2 size={20} className="mx-auto text-gold animate-spin" />
            <div className="w-full bg-cream/[0.06] rounded-full h-1.5">
              <div className="bg-gold h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-cream/30 text-[10px]">{progress >= 100 ? 'Done!' : 'Uploading...'}</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Icon size={20} className="mx-auto text-cream/20" />
            <p className="text-cream/30 text-xs">{label || `Drop ${accept === 'all' ? 'file' : accept} here or click`}</p>
            <p className="text-cream/15 text-[10px]">Max 50MB</p>
          </div>
        )}
      </div>

      {/* Browse library button */}
      <button
        onClick={openLibrary}
        className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] text-cream/25 hover:text-gold border border-transparent hover:border-gold/10 rounded transition-all"
        data-testid="browse-media-library"
      >
        <FolderOpen size={10} /> Browse Media Library
      </button>

      {error && (
        <div className="mt-2 flex items-center gap-1.5 text-red-400 text-[10px]">
          <X size={10} /> {error}
          <button onClick={() => setError('')} className="ml-auto text-cream/20 hover:text-cream/40"><X size={10} /></button>
        </div>
      )}

      {/* Library modal */}
      {showLibrary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowLibrary(false)}>
          <div className="bg-navy-deep border border-gold/[0.08] rounded-xl w-[90vw] max-w-2xl max-h-[70vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="media-library-modal">
            <div className="flex items-center justify-between p-4 border-b border-cream/[0.06]">
              <h3 className="text-cream text-sm font-semibold">Media Library</h3>
              <button onClick={() => setShowLibrary(false)} className="text-cream/30 hover:text-cream/60"><X size={16} /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[55vh]">
              {libraryLoading ? (
                <div className="py-10 text-center"><Loader2 size={20} className="mx-auto text-gold animate-spin" /></div>
              ) : libraryAssets.length === 0 ? (
                <p className="py-10 text-center text-cream/20 text-sm">No media found</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {libraryAssets.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => selectFromLibrary(a)}
                      className="group relative aspect-square bg-cream/[0.03] border border-cream/[0.06] rounded-lg overflow-hidden hover:border-gold/30 transition-all"
                      data-testid={`library-asset-${a.id}`}
                    >
                      {a.file_type === 'image' ? (
                        <img src={`${API_URL}${a.thumbnail_url || a.url}`} alt={a.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                          {a.file_type === 'video' ? <Film size={20} className="text-cream/20" /> : a.file_type === 'audio' ? <Music size={20} className="text-cream/20" /> : <Upload size={20} className="text-cream/20" />}
                          <span className="text-[8px] text-cream/20 px-1 truncate w-full text-center">{a.filename}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gold/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Check size={20} className="text-gold" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
